/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { omitBy } from 'lodash'

import {
  SvgIcon,
  Spinner
} from '../'

// Picklist based on https://lightningdesignsystem.com/components/picklist
// - provides a user with an read-only input field that is accompanied with a
// listbox of pre-defined options. A picklist has a single and multi-selection pattern.

export class Picklist extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    default: PropTypes.string,
    value: PropTypes.string,
    closeDropdownOnSelect: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    minWidth: PropTypes.number,
    maxHeight: PropTypes.number,
    symbol: PropTypes.string,
    inline: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string
  }

  static defaultProps = {
    symbol: 'down'
  }

  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
      selectedId: '',
      selectedOption: props.value ? props.value : props.default
    }

    this.picklistRef = React.createRef()
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState({ selectedOption: this.props.value || this.props.default })
    } else if (this.props.value === this.props.default && this.state.selectedOption !== this.props.default) {
      return { selectedOption: this.props.value }
    }
  }

  handleClick = (e) => {
    if (this.picklistRef.current.contains(e.target)) {
      // click is inside, picklist drop-down stays open
      return
    }

    // click is outside, close picklist drop-down
    this.setState({ isOpen: false })
  }

  handleSelect = (optionId, option) => {
    this.setState(prevState => (
      {
        selectedId: prevState.selectedId === optionId ? '' : optionId,
        selectedOption: prevState.selectedOption === option && option !== this.props.default ? '' : option,
        isOpen: this.props.closeDropdownOnSelect ? false : true
      }), () => {
        this.props.onChange(this.state.selectedOption)
      }
    )
  }

  render() {
    let uniqueId = this.props.id
    let options = this.props.options.map((option, index) => {
      let optionId = `${uniqueId}-option_${index}`

      return (
        <PicklistItem
          id={optionId}
          option={option}
          onSelect={() => this.handleSelect(optionId, option)}
          selected={this.state.selectedOption === option}
          disabled={this.props.disabled}
          key={optionId}
        />
      )
    })

    return(
      <div className={`slds-form-element ${this.props.className ? this.props.className : ''}`}>
        {this.props.label &&
          <label className="slds-form-element__label" htmlFor={`${this.props.id}_input`}>
            {this.props.label}
          </label>
        }
        <div className="slds-form-element__control">
          <div className="slds-combobox_container slds-size_small" style={{ minWidth: this.props.minWidth, width: 'inherit' }}>
            <div
              id={this.props.id}
              className={`slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click slds-combobox-picklist${this.state.isOpen ? " slds-is-open" : ''}`}
              aria-expanded={this.state.isOpen ? "true" : "false"}
              aria-haspopup="listbox"
              // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
              role="combobox"
              >
              <div
                className="slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right"
                onClick={() => this.setState((prevState) => (
                  {
                    isOpen: !prevState.isOpen && !this.props.disabled
                  }
                ))}>
                <input
                  type="text"
                  className={`slds-input slds-combobox__input${this.props.disabled ? " slds-text-color_weak" : ''}`}
                  id={`${uniqueId}_input`}
                  aria-activedescendant={this.state.selectedId}
                  aria-controls={`options_${uniqueId}`}
                  autoComplete="off"
                  placeholder={this.props.placeholder
                    ? this.props.placeholder
                    : this.props.inline
                      ? this.state.selectedOption
                      : "Select an option"
                  }
                  readOnly={true}
                  value={this.state.selectedOption}
                  style={this.props.inline
                    ? {
                        fontWeight: this.state.selectedOption !== this.props.default ? 'bold' : 'normal',
                        backgroundColor: 'inherit',
                        borderStyle: 'none',
                        paddingLeft: 0,
                        cursor: 'pointer',
                        // textTransform: 'capitalize'
                      }
                    : { cursor: 'pointer' }
                  }
                />
                <SvgIcon
                  variant="utility"
                  symbol={this.props.symbol}
                  colour={this.props.symbol === 'warning'
                    ? 'error'
                    : this.props.disabled ? "light" : "default"
                  }
                  size="xx-small"
                />
              </div>
              <div ref={this.picklistRef} id={`options_${uniqueId}`} role="listbox">
                <ul className="slds-listbox slds-listbox_vertical slds-dropdown slds-dropdown_fluid slds-scrollable_y" role="presentation" style={{ maxHeight: this.props.maxHeight || 'none' }}>
                  {options}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

const PicklistItem = (props) => {
  let uniqueId = props.id

  return (
    <li
      role="presentation"
      className="slds-listbox__item"
      onClick={() => props.onSelect(props.option)}>
      <span
        id={uniqueId}
        className={`slds-media slds-listbox__option slds-listbox__option_plain slds-media_small slds-media_center${props.selected ? " slds-is-selected" : ''}`}
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        role="option">
        <span className="slds-media__figure">
          <SvgIcon
            variant="utility"
            symbol="check"
            size="xx-small"
            className="slds-listbox__icon-selected"
          />
        </span>
        <span className="slds-media__body">
          <span className="slds-truncate" title={props.option}>
            {props.option}
          </span>
        </span>
      </span>
    </li>
  )
}

const getOptionsFromFetchedData = (props) => {
  let options = {}
  if (props.default) {
    options[props.default] = props.default.toLowerCase()
  }

  props.data.__type.enumValues.forEach(option => (
    options[option.description] = option.name.toLowerCase()
  ))

  return options
}

class PicklistData extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    default: PropTypes.string,
    value: PropTypes.string,
    closeDropdownOnSelect: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    minWidth: PropTypes.number,
    symbol: PropTypes.string,
    inline: PropTypes.bool,
    placeholder: PropTypes.string,
    excludeOptions: PropTypes.arrayOf(PropTypes.string)
  }

  static defaultProps = {
    default: '',
    excludeOptions: []
  }

  state = {
    options: this.props.data.loading === false  // data is already cached
      ? getOptionsFromFetchedData(this.props)
      : {},
    value: this.props.value
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data.loading && this.props.data.loading === false &&
      this.props.data.__type) {

      this.setState({ options: getOptionsFromFetchedData(this.props) })
    }
    if (this.props.value !== this.state.value) {
      this.setState({ value: this.props.value })
    }
  }

  isExcluded = (value) => this.props.excludeOptions.indexOf(value) > -1

  getOptions = () => {
    let options = null

    if (this.props.excludeOptions !== null && this.props.excludeOptions.length > 0) {
      options = Object.assign({}, omitBy(this.state.options, this.isExcluded))
    } else {
      options = this.state.options
    }

    return options
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner size="small" className="slds-is-relative" />
    }

    if (this.props.data.error) {
      return <div>Error loading options.</div>
    }

    let options = this.getOptions()
    let optionsKeyMatchingValue = Object.entries(options).find((kv) => kv[1] === this.props.value)

    return (
      <Picklist
        id={this.props.id}
        options={Object.keys(options)}
        default={this.props.default}
        symbol={this.props.symbol}
        value={this.props.value.length > 0
          ? options[this.props.value] === undefined
            ? optionsKeyMatchingValue && optionsKeyMatchingValue.length > 0
              ? optionsKeyMatchingValue[0]
              : this.props.default
            : options[this.props.value]
          : this.props.default
        }
        onChange={selectedOption => {
          selectedOption.length > 0
            ? this.props.onChange(options[selectedOption])
            : this.props.onChange(this.props.default.toLowerCase())
        }}
        closeDropdownOnSelect={this.props.closeDropdownOnSelect}
        inline={this.props.inline}
        placeholder={this.props.placeholder}
      />
    )
  }
}

const getOptions = gql`
  query($name: String!) {
    __type(name: $name) {
      name
      kind
      enumValues {
        name
        description
      }
    }
  }
`

const withData =
  graphql(getOptions, {
    options: (ownProps) => ({
      variables: {
        name: ownProps.forField
      },
      notifyOnNetworkStatusChange: true
    })
  })

export default withData(PicklistData)



// WEBPACK FOOTER //
// ./src/components/picklist/index.js